import H from 'history';
import { createContext, useContext } from 'react';
import { Action } from 'redux';

import { IResellerConfig } from '~/config/shared/reseller-config';
import { ECurrencyType } from '~/src/dash/api/exchange-rates';
import { mediaSize } from '~/src/dash/components/layout/common';
import { IStore } from '~/src/dash/reducers/store';
import { IDashboardsConfig } from '~/src/dash/views/dashboards/DashboardHome/configs/dashboardsConfig';
import { IDashboardOverviewConfig } from '~/src/dash/views/dashboards/DashboardHome/configs/dashboardOverviewConfig';
import { IDashboardLiveConfig } from '~/src/dash/views/dashboards/DashboardHome/configs/dashboardLiveConfig';
import { IDashboardParentOverviewConfig } from '~/src/dash/views/dashboards/DashboardHome/configs/dashboardParentOverviewConfig';
import { IDealsListViewConfig } from '~/src/dash/views/dealV2/DealsList/config';
import { IDashboardPublicaAnalyticsConfig } from '~/src/dash/views/dashboards/DashboardHome/configs/dashboardPublicaAnalyticsConfig';
import { IBiddersListViewConfig } from '~/src/dash/views/bidders/Bidders/tabs/BiddersListView/config';
import { IBidderGroupsListViewConfig } from '~/src/dash/views/bidders/Bidders/tabs/BidderGroupsListView/config';
import { IChannelsConfig } from '~/src/dash/views/channels/Channels/config';
import { INavigationConfig } from '~/src/dash/views/common';
import { IChannelGroupsConfig } from '~/src/dash/views/channels/ChannelGroups/config';
import { IChannelMappingsConfig } from '~/src/dash/views/channels/ChannelMappings/config';
import { IPendingOffersConfig } from '~/src/dash/views/platform-connect/PendingOffers/config';
import { IConnectionsConfig } from '~/src/dash/views/platform-connect/Connections/config';
import { IOfferNewConfig } from '~/src/dash/views/platform-connect/DraftOfferNew/config';
import { IAudienceManagerListViewConfig } from '~/src/dash/views/segments/audience/AudienceManagerSettingsListView/config';
import { ICreativeReviewListViewConfig } from '~/src/dash/views/brand-safety/creative-review/config';
import { IInventoryParametersFormConfig } from '~/src/dash/views/tools/Forecasting/InventoryParameters/config';
import { ITargetingPresetListViewConfig } from '~/src/dash/views/tools/TargetingPresets/config';
import { ITargetingPresetFormConfig } from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/config';
import { ILiverampDistributionViewConfig } from '~/src/dash/views/segments/audience/liveramp/distribution/config';
import { TKey } from '~/src/dash/publicaUI';
import { IReportingListConfig } from '~/src/dash/views/reporting/ReportingListView/config';
import { ILineItemsListViewConfig } from '~/src/dash/views/campaigns/line-items/LineItemsListView/config';
import { IOrdersListViewConfig } from '~/src/dash/views/campaigns/orders/OrdersListView/config';
import { IOrderLineItemsListViewConfig } from '~/src/dash/views/campaign/OrderLineItemsListView/config';
import { IDashboardAuctionInsightsConfig } from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardAuctionInsights/configs/mainCfg';
import { IInventoryRulesListConfig } from '~/src/dash/views/inventory-rules-v2/Rules/InventoryRulesList/config';

interface IBaseStore extends IStore {
  location: H.Location;
  isSuperUser: boolean;
  isDemoUser: boolean;
  children: any;
  currencyMultiplier: number;
  currencySymbol: string;
  currencyType: ECurrencyType;
  dispatch: (action: Action<any>) => any;
  resellerCfg: IResellerConfig;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: TKey, props?: Record<string, any>) => string;
  [key: string]: any;
}

export const BaseContext = createContext({} as IBaseStore);
export const useBaseStore = (): IBaseStore => useContext(BaseContext);

export interface IBaseState {
  isNavExpanded: boolean;
  totalWidth: number | null;
  totalHeight: number | null;
  mediaSize: mediaSize;
}

export interface IBaseConfig {
  AudienceSegments: {
    AudienceSegments: {
      Main: INavigationConfig;
    };
  };
  Bidders: {
    Main: INavigationConfig;
    All: IBiddersListViewConfig;
    Groups: IBidderGroupsListViewConfig;
  };
  BrandSafety: {
    CreativeReview: {
      Main: ICreativeReviewListViewConfig;
    };
  };
  Campaigns: {
    LineItems: {
      Main: ILineItemsListViewConfig;
      OrderLineItems: IOrderLineItemsListViewConfig;
    };
    Orders: {
      Main: IOrdersListViewConfig;
    };
  };
  Channels: {
    Main: INavigationConfig;
    All: IChannelsConfig;
    Groups: IChannelGroupsConfig;
    Mappings: IChannelMappingsConfig;
  };
  Dashboards: {
    Main: IDashboardsConfig;
    Overview: IDashboardOverviewConfig;
    Live: IDashboardLiveConfig;
    ParentOverview: IDashboardParentOverviewConfig;
    PublicaAnalytics: IDashboardPublicaAnalyticsConfig;
    AuctionInsights: IDashboardAuctionInsightsConfig;
  };
  Deals: {
    Main: IDealsListViewConfig;
  };
  InventoryRules: {
    Rules: {
      Main: INavigationConfig;
      All: IInventoryRulesListConfig;
    };
  };
  LiverampDistribution: {
    Main: ILiverampDistributionViewConfig;
  };
  PlatformConnect: {
    Main: INavigationConfig;
    PendingOffers: IPendingOffersConfig;
    Connections: IConnectionsConfig;
    DraftOfferNew: IOfferNewConfig;
  };
  Reporting: {
    Main: IReportingListConfig;
  };
  Segments: {
    Audience: {
      Main: IAudienceManagerListViewConfig;
    };
  };
  Tools: {
    Forecasting: {
      Main: {
        InventoryParameters: IInventoryParametersFormConfig;
      };
    };
    Targeting: {
      Main: ITargetingPresetListViewConfig;
      Form: ITargetingPresetFormConfig;
    };
  };
}

export const BaseContextState = createContext({} as IBaseState);
export const useBaseState = (): IBaseState => useContext(BaseContextState);

export const BaseConfig = createContext({} as IBaseConfig);
export const useBaseConfig = (): IBaseConfig => useContext(BaseConfig);
